import styled, { css } from 'styled-components';
import { RichText } from '@sitecore-jss/sitecore-jss-react';

import { Link } from '_utils/components';
import { spacing } from '_utils/props';
import {
  button,
  depth,
  focusOutline,
  hideOnPrint,
  mq,
  safeFocusRemoval,
  svg,
} from '_utils/styles';

const announcementThemes = {
  Blue: css`
    background-color: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.blue.background};
    border-bottom: 1px solid;
    border-color: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.blue.borderColor};

    path {
      fill: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.pathFillColor};
    }
  `,
  Yellow: css`
    background-color: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.yellow.background};
    border-bottom: 1px solid;
    border-color: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.yellow.borderColor};

    path {
      fill: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.pathFillColor};
    }
  `,
  Red: css`
    background-color: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.red.background};
    border-bottom: 1px solid;
    border-color: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.red.borderColor};

    path {
      fill: ${({ theme }) => theme.siteWideAnnouncement.announcementThemes.pathFillColor};
    }
  `
};

const handleAnnouncementTheme = ({ alertStyle }) => announcementThemes[alertStyle];

/**
 * Dismissal runs in two overlapping phases so the collapse reads as smooth:
 *
 *  1. the content fades out quickly, and
 *  2. the height collapses afterwards, on a longer, gentler curve.
 *
 * The order matters. `overflow: hidden` clips the child from the bottom, so a
 * height collapse on its own looks like the text is being guillotined rather
 * than leaving. Fading first means that clipping happens while there is
 * nothing left to see, and all the eye follows is the page below sliding up.
 */
export const DISMISS_FADE_MS = 120;
export const DISMISS_COLLAPSE_DELAY_MS = 60;
export const DISMISS_COLLAPSE_MS = 280;
export const DISMISS_DURATION_MS = DISMISS_COLLAPSE_DELAY_MS + DISMISS_COLLAPSE_MS;

/**
 * The banner's height is content-driven (it depends on how long the authored
 * copy is), and `height` is not animatable from `auto`, so the component
 * measures the rendered height and writes it on as an explicit pixel value
 * before transitioning it to zero.
 *
 * An earlier version animated `grid-template-rows` from `1fr` to `0fr`, which
 * avoids the measuring step. Don't go back to it: resolving an `fr` inside an
 * auto-height container runs the grid track sizing algorithm, which does not
 * interpolate linearly across the 0-1 range, and the collapse visibly steps.
 * Pixels interpolate exactly.
 *
 * `overflow` is only hidden while dismissing so it can't clip focus outlines
 * on the close button during normal use.
 */
export const CollapseWrapper = styled.div`
  opacity: 1;
  transition:
    height ${DISMISS_COLLAPSE_MS}ms cubic-bezier(0.4, 0, 0.2, 1) ${DISMISS_COLLAPSE_DELAY_MS}ms,
    opacity ${DISMISS_FADE_MS}ms ease-out;

  ${({ isDismissing }) => isDismissing && css`
    overflow: hidden;
    opacity: 0;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const AnnouncementWrapper = styled.div`
  ${handleAnnouncementTheme}

  align-items: flex-start;
  color: ${({ theme }) => theme.siteWideAnnouncement.announcementWrapper.color};
  display: flex;
  position: relative;
  z-index: ${depth.HEADER - 1};

  ${({ theme }) => spacing({ theme, p: { xs: 3, md: 2 } })};

  @media print {
    path {
      fill: ${({ theme }) => theme.siteWideAnnouncement.announcementWrapper.pathFillColor};
    }
  }
`;

export const IconWrapper = styled.div`
  ${svg(20, 20)}
  width: 20px;
  flex-shrink: 0;
  position: relative;
  top: 0px;

  ${({ theme }) => spacing({ theme, mb: { xs: '18px', md: 0 } })};

  ${mq('md')} {
    top: 2px;
  }
`;

export const TextWrapper = styled.div`
  ${({ theme }) => spacing({ theme, ml: 2 })};
  width: calc(100% - 60px);
`;

export const TitleWrapper = styled.h2`
  ${({ theme }) => theme.siteWideAnnouncement.titleWrapper.heading2};
  color: inherit;
  margin: 0;
  font-weight: bold !important;
  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }

  &:hover {
    text-decoration: none;
  }
`;

export const Content = styled(RichText)`
  ${({ theme }) => theme.siteWideAnnouncement.content.richText};
  margin: 0;
  ${({ theme }) => spacing({ theme, mt: '4px' })};

  a {
    color: inherit;
    &:hover {
      text-decoration: none;
    }
  }
`;

export const Trigger = styled.button`
  border: 0;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  height: 24px;
  padding: 0;
  position: absolute;
  right: 16px;
  top: 16px;
  ${({ theme }) => spacing({ theme, ml: 1 })};
  ${hideOnPrint}

  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`

export const AnnouncementCTA = styled(Link)`
  ${({ theme }) => spacing({ theme, mt: '12px', mr: '16px' })};

  a {
    ${button('slim', 'announcementTertiary')};
    ${({ theme }) => theme.siteWideAnnouncement.announcementCta.heading2};

    ${mq.lessThan('md')} {
      width: 100%;
    }

    path {
      fill: currentColor;
    }
  }
`;

export const SecondaryButton = styled(Link)`
  ${({ theme }) => spacing({ theme, mt: '12px' })};
  
  a {
    ${({ theme }) => theme.siteWideAnnouncement.announcementCta.heading2};
    ${mq.lessThan('md')} {
      width: 100%;
    }
    color: inherit;
    path {
      fill: currentColor;
    }
  }
`;
